import type { ColumnDef } from "@tanstack/react-table";
import { Link } from "react-router-dom";
import { importBatchPath } from "../../lib/paths";
import { PiEyeDuotone as EyeIcon } from "react-icons/pi";
import { AvatarCard, getStatusBadge } from "@steelyard/theme";
import { formatMoment } from "../../lib/format";
import { RowActions } from "../../components/table/row-actions";
import type { ImportBatchListItem } from "../../lib/services/catalog";

const KIND_LABEL: Record<ImportBatchListItem["kind"], string> = {
  catalogue: "Catalogue import",
  tier_price: "Tier-price import",
};

/** A batch has no number of its own: it reads as its kind and when it was uploaded. */
const batchLabel = (batch: ImportBatchListItem) => `${KIND_LABEL[batch.kind]} · ${formatMoment(batch.uploadedAt)}`;

/**
 * One past batch of `GET /imports` (`ImportBatchListView`). The four counts sit beside each other
 * rather than summed into one figure — the same "never a merged total" rule `Diff.tsx`'s own four
 * cards follow for the same numbers on one batch.
 */
function RowCounts({ batch }: { batch: ImportBatchListItem }) {
  return (
    <span className="flex flex-wrap gap-2 font-mono text-xs">
      <span className="text-green-dark">+{batch.additionCount}</span>
      <span className="text-orange-dark">~{batch.changeCount}</span>
      <span className="text-red-dark">!{batch.errorCount}</span>
      <span className="text-gray-500">={batch.unchangedCount}</span>
    </span>
  );
}

export const importHistoryColumns: ColumnDef<ImportBatchListItem>[] = [
  {
    id: "batch",
    accessorFn: batchLabel,
    header: "Batch",
    size: 280,
    cell: ({ row }) => (
      <Link to={importBatchPath(row.original.batchNumber || row.original.batchId)} className="hover:underline">
        <AvatarCard src="" name={KIND_LABEL[row.original.kind]} description={formatMoment(row.original.uploadedAt)} />
      </Link>
    ),
  },
  {
    id: "uploadedBy",
    accessorFn: (batch) => batch.uploadedByName ?? "",
    header: "Uploaded by",
    size: 200,
    cell: ({ row }) => {
      if (!row.original.uploadedBy) return <span className="text-muted-foreground italic">not recorded</span>;
      // Catalog sends the name; an erased or unresolved account reads "Unknown user", never its id.
      return <span>{row.original.uploadedByName ?? "Unknown user"}</span>;
    },
  },
  {
    id: "uploadedAt",
    accessorKey: "uploadedAt",
    header: "Uploaded at",
    size: 200,
    cell: ({ row }) => <span className="text-gray-600">{formatMoment(row.original.uploadedAt)}</span>,
  },
  {
    id: "rows",
    header: "Rows (added / changed / errors / unchanged)",
    size: 200,
    cell: ({ row }) => <RowCounts batch={row.original} />,
  },
  {
    id: "status",
    accessorKey: "status",
    header: "Status",
    size: 140,
    cell: ({ row }) => getStatusBadge(row.original.status),
  },
  {
    id: "actions",
    header: "",
    size: 90,
    cell: ({ row }) => (
      <RowActions
        name={batchLabel(row.original)}
        primary={{ label: "View diff", icon: EyeIcon, to: importBatchPath(row.original.batchNumber || row.original.batchId) }}
      />
    ),
  },
];
